const isDevelopment = process.env.NODE_ENV === 'development';
const AUTH_BASE = isDevelopment ? 'http://localhost:8085' : '/api/auth';

async function authRequest(path: string, options: RequestInit = {}, _retry = false): Promise<Response> {
  const { getToken, clearToken } = await import('./tokenStore');
  const { refresh } = await import('./authApi');
  const token = getToken();
  const res = await fetch(`${AUTH_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers as Record<string, string> || {}),
    },
  });
  if (res.status === 401 && !_retry) {
    const user = await refresh().catch(() => null);
    if (user) return authRequest(path, options, true);
    clearToken();
  }
  return res;
}

export interface Project {
  id: string;
  name: string;
  description: string;
  owner_id: string;
  created_at: string;
  role?: string;
}

export interface ProjectMember {
  user_id: string;
  username: string;
  display_name: string;
  email: string;
  role: 'owner' | 'admin' | 'member';
}

export async function listProjects(): Promise<Project[]> {
  const res = await authRequest('/projects');
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function createProject(name: string, description: string): Promise<Project> {
  const res = await authRequest('/projects', {
    method: 'POST',
    body: JSON.stringify({ name, description }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getProject(projectId: string): Promise<Project> {
  const res = await authRequest(`/projects/${projectId}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function updateProject(projectId: string, name: string, description: string): Promise<Project> {
  const res = await authRequest(`/projects/${projectId}`, {
    method: 'PUT',
    body: JSON.stringify({ name, description }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function deleteProject(projectId: string): Promise<void> {
  const res = await authRequest(`/projects/${projectId}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(await res.text());
}

export async function listMembers(projectId: string): Promise<ProjectMember[]> {
  const res = await authRequest(`/projects/${projectId}/members`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function addMember(projectId: string, userId: string, role: string): Promise<void> {
  const res = await authRequest(`/projects/${projectId}/members`, {
    method: 'POST',
    body: JSON.stringify({ user_id: userId, role }),
  });
  if (!res.ok) throw new Error(await res.text());
}

export async function updateMemberRole(projectId: string, userId: string, role: string): Promise<void> {
  const res = await authRequest(`/projects/${projectId}/members/${userId}`, {
    method: 'PUT',
    body: JSON.stringify({ role }),
  });
  if (!res.ok) throw new Error(await res.text());
}

export async function removeMember(projectId: string, userId: string): Promise<void> {
  const res = await authRequest(`/projects/${projectId}/members/${userId}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(await res.text());
}

export interface UserSearchResult {
  id: string;
  username: string;
  email: string;
}

export async function searchUsers(query: string): Promise<UserSearchResult[]> {
  const res = await authRequest(`/users/search?q=${encodeURIComponent(query)}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function initProject(projectId: string): Promise<void> {
  const { apiRequest } = await import('../config/api');
  await apiRequest(`/projects/${projectId}/init`, { method: 'POST' });
}
